module Main exposing (..)

import Browser
import Dict
import Html exposing (Html, div)
import Html.Attributes exposing (class, classList, style)
import Html.Events exposing (onClick)
import List.Extra
import Process
import Random
import Task



--TODO:
-- 1. Animation falling tiles
-- 2. Animation new falling tiles and update grid
-- 3. Check if matches are possible
-- CONSTANTS


gridSize : Int
gridSize =
    8


pokemonCssClasses : ( String, List String )
pokemonCssClasses =
    ( "pokemon-1"
    , [ "pokemon-2"
      , "pokemon-3"
      , "pokemon-4"
      , "pokemon-5"
      , "pokemon-6"
      , "pokemon-7"
      ]
    )



-- MAIN


main : Program () Model Msg
main =
    Browser.element
        { init = always init
        , update = update
        , view = view
        , subscriptions = \_ -> Sub.none
        }



-- MODEL


type Tile
    = CssClass String


type alias Grid =
    List Tile


type Pos
    = Pos Int


randomGrid : Random.Generator (List Tile)
randomGrid =
    let
        replaceMatches g =
            case findMatches g of
                [] ->
                    if hasPossibleMove g then
                        Random.constant g

                    else
                        Random.list (gridSize * gridSize) randomTile
                            |> Random.andThen replaceMatches

                matches ->
                    let
                        matchingPositions =
                            List.concatMap identity matches
                                |> List.Extra.unique

                        replaceTiles tiles =
                            let
                                tilesDict =
                                    List.Extra.zip matchingPositions tiles
                                        |> List.map (\( Pos i, tile ) -> ( i, tile ))
                                        |> Dict.fromList
                            in
                            gridPositions g
                                |> List.map (\( Pos i, tile ) -> Dict.get i tilesDict |> Maybe.withDefault tile)
                    in
                    Random.list (List.length matchingPositions) randomTile
                        |> Random.map replaceTiles
                        |> Random.andThen replaceMatches
    in
    Random.list (gridSize * gridSize) randomTile
        |> Random.andThen replaceMatches


randomTile : Random.Generator Tile
randomTile =
    let
        ( head, tail ) =
            pokemonCssClasses
    in
    Random.uniform head tail |> Random.map CssClass


hasPossibleMove : Grid -> Bool
hasPossibleMove grid =
    let
        gridWithPositions =
            gridPositions grid

        -- Helper to check if swapping two tiles results in a match
        isMovePossible (Pos i1) (Pos i2) =
            let
                swappedGrid =
                    List.Extra.swapAt i1 i2 grid
            in
            not (List.isEmpty (findMatches swappedGrid))

        -- Get all adjacent positions for a given position
        adjacentPositions (Pos i) =
            [ Pos (i - 1) -- Left
            , Pos (i + 1) -- Right
            , Pos (i - gridSize) -- Up
            , Pos (i + gridSize) -- Down
            ]
                |> List.filter (\(Pos adj) -> adj >= 0 && adj < List.length grid)

        -- Check all possible swaps for each tile
        possibleMoves =
            List.any
                (\( pos, _ ) ->
                    List.any (isMovePossible pos)
                        (adjacentPositions pos)
                )
                gridWithPositions
    in
    possibleMoves


type GridState
    = Normal
    | Selected Pos
    | Swapping Pos Pos Direction
    | ShowMatches (List Pos)


type alias Model =
    { grid : Grid
    , gridState : GridState
    }


init : ( Model, Cmd Msg )
init =
    ( { grid = []
      , gridState = Normal
      }
    , Random.generate NewGrid randomGrid
    )



-- UPDATE


type Msg
    = NewGrid Grid
    | Click Pos
    | SwapEnd


update : Msg -> Model -> ( Model, Cmd Msg )
update msg model =
    case ( msg, model.gridState ) of
        ( NewGrid g, _ ) ->
            ( { model | grid = g }, Cmd.none )

        ( Click pos, Normal ) ->
            ( { model | gridState = Selected pos }, Cmd.none )

        ( Click pos, Selected oldPos ) ->
            case getRelation oldPos pos of
                Adjacent d ->
                    ( { model | gridState = Swapping oldPos pos d }, Process.sleep 500 |> Task.perform (always SwapEnd) )

                Self ->
                    ( { model | gridState = Normal }, Cmd.none )

                Other ->
                    ( { model | gridState = Selected pos }, Cmd.none )

        ( SwapEnd, Swapping (Pos p1) (Pos p2) _ ) ->
            ( let
                newGrid =
                    List.Extra.swapAt p1 p2 model.grid
              in
              case findMatches newGrid of
                [] ->
                    { model | gridState = Normal }

                matches ->
                    { model
                        | grid = newGrid
                        , gridState =
                            matches
                                |> List.concatMap identity
                                |> List.Extra.unique
                                |> ShowMatches
                    }
            , Cmd.none
            )

        _ ->
            ( model, Cmd.none )


findMatches : Grid -> List (List Pos)
findMatches grid =
    let
        merge ( a, b ) =
            a :: b

        gridWithPositions =
            gridPositions grid

        -- Helper to group positions by rows
        groupByRow =
            List.Extra.gatherEqualsBy (\( Pos i, _ ) -> i // gridSize)

        -- Helper to group positions by columns
        groupByColumn =
            List.Extra.gatherEqualsBy (\( Pos i, _ ) -> remainderBy gridSize i)

        -- Helper to find matches in a single line (row or column)
        findLineMatches line =
            let
                grouped =
                    List.Extra.groupWhile (\( _, CssClass c1 ) ( _, CssClass c2 ) -> c1 == c2) line

                matches =
                    grouped |> List.map merge |> List.filter (\group -> List.length group >= 3)
            in
            List.map (List.map (\( pos, _ ) -> pos)) matches

        -- Find matches in rows and columns
        rowMatches =
            groupByRow gridWithPositions |> List.map merge |> List.concatMap findLineMatches

        columnMatches =
            groupByColumn gridWithPositions |> List.map merge |> List.concatMap findLineMatches
    in
    rowMatches ++ columnMatches



-- VIEW


view : Model -> Html Msg
view model =
    div
        [ style "display" "flex"
        , style "min-height" "100vh"
        , style "justify-content" "center"
        , style "align-items" "center"
        , style "flex-direction" "column"
        ]
        [ div
            [ style "display" "grid"
            , style "grid-template-columns" ("repeat(" ++ String.fromInt gridSize ++ ", auto)")
            ]
            (List.map (viewTile model.gridState) <| gridPositions model.grid)
        ]


gridPositions : Grid -> List ( Pos, Tile )
gridPositions grid =
    List.indexedMap (\i tile -> ( Pos i, tile )) grid


viewTile : GridState -> ( Pos, Tile ) -> Html Msg
viewTile gridState ( pos, CssClass c ) =
    let
        dir =
            case gridState of
                Swapping p1 p2 d ->
                    if p1 == pos then
                        Just d

                    else if p2 == pos then
                        Just (opposite d)

                    else
                        Nothing

                _ ->
                    Nothing

        toStr d =
            case d of
                Just Left ->
                    [ style "transform" "translateX(-80px)", style "transition" "transform 0.5s" ]

                Just Right ->
                    [ style "transform" "translateX(80px)", style "transition" "transform 0.5s" ]

                Just Up ->
                    [ style "transform" "translateY(-80px)", style "transition" "transform 0.5s" ]

                Just Down ->
                    [ style "transform" "translateY(80px)", style "transition" "transform 0.5s" ]

                Nothing ->
                    []
    in
    div
        ([ class "tile"
         , class c
         , classList
            [ ( "tile-selected", gridState == Selected pos )
            , ( "tile-matched"
              , case gridState of
                    ShowMatches matches ->
                        List.member pos matches

                    _ ->
                        False
              )
            ]
         , onClick (Click pos)
         ]
            ++ toStr dir
        )
        []


type Direction
    = Left
    | Right
    | Up
    | Down


type Relation
    = Adjacent Direction
    | Self
    | Other


getRelation : Pos -> Pos -> Relation
getRelation (Pos x1) (Pos x2) =
    if x1 == x2 then
        Self

    else if x2 == x1 + 1 && (x1 // gridSize) == (x2 // gridSize) then
        Adjacent Right

    else if x2 == x1 - 1 && (x1 // gridSize) == (x2 // gridSize) then
        Adjacent Left

    else if x2 == x1 + gridSize then
        Adjacent Down

    else if x2 == x1 - gridSize then
        Adjacent Up

    else
        Other


opposite : Direction -> Direction
opposite d =
    case d of
        Left ->
            Right

        Right ->
            Left

        Up ->
            Down

        Down ->
            Up
